import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { beoordeelWaarde, beoordeelCombinaties } from '../utils/klinischeValidatie';
import styles from './ABCDE.module.css';

const STAPPEN = [
  { id:0, code:'0', label:'Beller' },
  { id:1, code:'A', label:'Airway' },
  { id:2, code:'B', label:'Breathing' },
  { id:3, code:'C', label:'Circulation' },
  { id:4, code:'D', label:'Disability' },
  { id:5, code:'E', label:'Exposure' },
  { id:6, code:'Ø', label:'Afsluiting' },
];

const LEEG = {
  beller_naam:'', beller_functie:'', beller_afdeling:'', contact_met:'',
  luchtweg_vrij:'', luchtweg_hulpmiddel:'', stridor:'', secreties:'',
  ademfrequentie:'', o2_saturatie:'', o2_gebruik:'', o2_liter:'',
  adempatroon:'', hulpademhalingsspieren:'', cyanose:'',
  bloeddruk_sys:'', bloeddruk_dia:'', hartslag:'', hartritme:'',
  temperatuur:'', huidkleur:'', capillaire_refill:'', bloeding:'',
  avpu:'', bloedsuiker:'', pijnscore:'', pupilreactie:'',
  neurologische_uitval:'', neurologische_uitval_omschrijving:'',
  huidafwijkingen:'', huidafwijkingen_omschrijving:'',
  zwelling_roodheid:'', zwelling_locatie:'',
  recente_gebeurtenissen:'', voorgeschiedenis:'', medicatie:'',
};

export default function ABCDE({ onOutputGenereren }) {
  const { gebruiker, heeftModule } = useAuth();
  const [stap, setStap]   = useState(0);
  const [invoer, setInvoer] = useState(LEEG);
  const [waarschuwingen, setWaarschuwingen]     = useState({});
  const [combinatieW, setCombinatieW]           = useState([]);
  const [vervolgvragen, setVervolgvragen]       = useState([]);
  const [urgentie, setUrgentie]                 = useState('');
  const [urgentieAi, setUrgentieAi]             = useState('');
  const [urgentieWaarschuwing, setUrgentieW]    = useState('');
  const [outputType, setOutputType]             = useState('sbar');
  const [verzoek, setVerzoek]                   = useState('');
  const [omschrijving, setOmschrijving]         = useState('');
  const [fout, setFout]                         = useState('');
  const [laden, setLaden]                       = useState(false);

  useEffect(() => {
    const opgeslagen = sessionStorage.getItem('alyden_invoer');
    if (opgeslagen) { try { setInvoer(JSON.parse(opgeslagen)); } catch {} }
  }, []);

  // Laad standaardwaarden beller uit profiel
  useEffect(() => {
    axios.get('/api/profiel').then(res => {
      const p = res.data.profiel;
      if (p.beller_actief) {
        setInvoer(prev => {
          // Alleen invullen als veld nog leeg is
          const updates = {};
          if (!prev.beller_naam    && p.beller_naam)    updates.beller_naam    = p.beller_naam;
          if (!prev.beller_functie && p.beller_functie) updates.beller_functie = p.beller_functie;
          if (!prev.beller_afdeling && p.beller_afdeling_id && res.data.afdelingen) {
            const afd = res.data.afdelingen.find(a => a.id === p.beller_afdeling_id);
            if (afd) updates.beller_afdeling = afd.naam;
          }
          return Object.keys(updates).length ? { ...prev, ...updates } : prev;
        });
      }
    }).catch(() => {});
  }, []);

  useEffect(() => {
    sessionStorage.setItem('alyden_invoer', JSON.stringify(invoer));
  }, [invoer]);

  useEffect(() => {
  window.scrollTo({ top: 0, behavior: 'smooth' });
}, [stap]);

  const updateW = useCallback((nInvoer) => {
    const w = {};
    ['ademfrequentie','o2_saturatie','hartslag','bloeddruk_sys','temperatuur','bloedsuiker','pijnscore']
      .forEach(v => { const b = beoordeelWaarde(v, nInvoer[v]); if (b) w[v] = b; });
    setWaarschuwingen(w);
    setCombinatieW(beoordeelCombinaties(nInvoer));
  }, []);

  function setVeld(veld, waarde) {
    const n = { ...invoer, [veld]: waarde };
    setInvoer(n);
    updateW(n);
  }

  async function laadVervolgvragen() {
    try {
      const r = await axios.post('/api/sessies/vervolgvragen', { invoer, urgentie });
      setVervolgvragen(r.data.vragen || []);
    } catch {}
  }

  async function valideerUrgentie(keuze) {
    setUrgentie(keuze);
    try {
      const r = await axios.post('/api/sessies/urgentie-valideer', { keuze, invoer });
      setUrgentieAi(r.data.aiSuggestie);
      setUrgentieW(r.data.waarschuwingen?.[0]?.bericht || '');
    } catch {}
  }

  function volgende() { setFout(''); if (stap === 5) laadVervolgvragen(); setStap(s => Math.min(s+1, 6)); }
  function terug()    { setFout(''); setStap(s => Math.max(s-1, 0)); }

  async function handleGenereer() {
    if (!omschrijving.trim()) return setFout('Geef een korte omschrijving van de situatie.');
    if (!urgentie)    return setFout('Kies een urgentieniveau.');
    if (!outputType)  return setFout('Kies een outputtype.');
    setLaden(true); setFout('');
    try {
      const r = await axios.post('/api/sessies', {
        invoer, urgentie, urgentie_ai: urgentieAi,
        gebruiker_override: urgentieAi && urgentieAi !== urgentie ? 1 : 0,
        omschrijving,
      });
      sessionStorage.removeItem('alyden_invoer');
      onOutputGenereren({ sessie_id: r.data.sessie_id, invoer, urgentie, urgentieAi, output_type: outputType, verzoek, omschrijving, combinatieWaarschuwingen: combinatieW });
    } catch (err) {
      setFout(err.response?.data?.error || 'Genereren mislukt. Controleer uw verbinding.');
    } finally { setLaden(false); }
  }

  function W({ veld }) {
    const w = waarschuwingen[veld];
    if (!w) return null;
    return <div className={`${styles.waarschuwing} ${w.niveau === 'kritisch' ? styles.kritisch : styles.afwijkend}`}>{w.niveau === 'kritisch' ? '🔴' : '🟡'} {w.bericht}</div>;
  }

  function JaNee({ veld, gevaar }) {
    return (
      <div className={styles.knoprij}>
        {['Ja','Nee'].map(o => (
          <button key={o}
            className={`${styles.keuzeKnopKlein} ${invoer[veld]===o ? styles.geselecteerd:''} ${gevaar && o==='Ja' ? styles.gevaar:''}`}
            onClick={() => setVeld(veld, o)}>{o}</button>
        ))}
      </div>
    );
  }

  const oefenmodus = gebruiker?.oefenmodus_actief === 1 || gebruiker?.oefenmodus_actief === true;

  return (
    <div className={`${styles.pagina} ${oefenmodus ? styles.oefenmodus : ''}`}>
      {oefenmodus && <div className={styles.oefenBanner}>📚 Oefenmodus actief — invoer wordt niet opgeslagen als klinische data</div>}
      {combinatieW.length > 0 && combinatieW.map((w,i) => <div key={i} className={`${styles.waarschuwing} ${styles.kritisch} ${styles.combinatie}`}>{w.bericht}</div>)}

      {/* Voortgang */}
      <div className={styles.voortgang}>
        {STAPPEN.map(s => (
          <div key={s.id} className={`${styles.stapItem} ${stap===s.id?styles.actief:''} ${stap>s.id?styles.klaar:''}`}>
            <div className={styles.stapCircel}>{s.code}</div>
            <span className={styles.stapLabel}>{s.label}</span>
          </div>
        ))}
      </div>

      {/* STAP 0: BELLER */}
      {stap === 0 && (
        <div className={styles.stap}>
          <h2 className={styles.stapTitel}>Wie belt?</h2>
          <div className={styles.kaart}>
            <div className={styles.veld}><label>Naam</label>
              <input type="text" value={invoer.beller_naam} onChange={e=>setVeld('beller_naam',e.target.value)} placeholder="Voornaam of volledige naam" autoFocus /></div>
            <div className={styles.veld}><label>Functie</label>
              <select value={invoer.beller_functie} onChange={e=>setVeld('beller_functie',e.target.value)}>
                <option value="">Kies functie...</option>
                {['Verpleegkundige','Verzorgende','Helpende +','Helpende'].map(o=><option key={o}>{o}</option>)}
              </select></div>
            <div className={styles.veld}><label>Afdeling <span className={styles.optioneel}>(optioneel)</span></label>
              <input type="text" value={invoer.beller_afdeling} onChange={e=>setVeld('beller_afdeling',e.target.value)} placeholder="Naam van de afdeling" /></div>
          </div>
          <div className={styles.kaart}>
            <label className={styles.groepLabel}>Contact met</label>
            <div className={styles.knoprij}>
              {[{c:'DV',s:'Dienstdoende Verpleegkundige'},{c:'SO',s:'Specialist Ouderengeneeskunde'},{c:'Arts',s:'Huisarts / specialist'},{c:'Andere',s:'Anders'}].map(o=>(
                <button key={o.c} className={`${styles.keuzeKnop} ${invoer.contact_met===o.c?styles.geselecteerd:''}`} onClick={()=>setVeld('contact_met',o.c)}>
                  <span className={styles.keuzeHoofd}>{o.c}</span><span className={styles.keuzeOnd}>{o.s}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* STAP 1: AIRWAY */}
      {stap === 1 && (
        <div className={styles.stap}>
          <h2 className={styles.stapTitel}>A — Airway</h2>
          <div className={styles.kaart}>
            <div className={styles.veld}><label>Luchtweg vrij?</label>
              <div className={styles.knoprij}>
                {['Ja','Gedeeltelijk','Nee'].map(o=>(
                  <button key={o} className={`${styles.keuzeKnopKlein} ${invoer.luchtweg_vrij===o?styles.geselecteerd:''} ${o==='Nee'?styles.gevaar:''}`} onClick={()=>setVeld('luchtweg_vrij',o)}>{o}</button>
                ))}
              </div>
            </div>
            <div className={styles.veld}><label>Hulpmiddelen <span className={styles.optioneel}>(optioneel)</span></label>
              <select value={invoer.luchtweg_hulpmiddel} onChange={e=>setVeld('luchtweg_hulpmiddel',e.target.value)}>
                <option value="">Geen / niet van toepassing</option>
                {['Zuurstofbril','Zuurstofmasker','Canule','Andere'].map(o=><option key={o}>{o}</option>)}
              </select>
            </div>
            <div className={styles.tweeKolommen}>
              <div className={styles.veld}><label>Gierende/piepende ademhaling (stridor)?</label><JaNee veld="stridor" gevaar /></div>
              <div className={styles.veld}><label>Slijm/secreties in de luchtwegen?</label><JaNee veld="secreties" /></div>
            </div>
          </div>
        </div>
      )}

      {/* STAP 2: BREATHING */}
      {stap === 2 && (
        <div className={styles.stap}>
          <h2 className={styles.stapTitel}>B — Breathing</h2>
          <div className={styles.vitaalGrid}>
            <div className={styles.vitaalKaart}>
              <label>Ademfrequentie</label>
              <input type="number" inputMode="numeric" value={invoer.ademfrequentie} onChange={e=>setVeld('ademfrequentie',e.target.value)} placeholder="16" min="0" max="60" />
              <span className={styles.eenheid}>adem/min</span><W veld="ademfrequentie" />
            </div>
            <div className={styles.vitaalKaart}>
              <label>O2-saturatie</label>
              <input type="number" inputMode="numeric" value={invoer.o2_saturatie} onChange={e=>setVeld('o2_saturatie',e.target.value)} placeholder="97" min="50" max="100" />
              <span className={styles.eenheid}>%</span><W veld="o2_saturatie" />
            </div>
          </div>
          <div className={styles.kaart}>
            <div className={styles.veld}><label>Zuurstofgebruik?</label><JaNee veld="o2_gebruik" /></div>
            {invoer.o2_gebruik==='Ja' && (
              <div className={styles.veld}><label>Hoeveel liter?</label>
                <input type="number" inputMode="decimal" step="0.5" value={invoer.o2_liter} onChange={e=>setVeld('o2_liter',e.target.value)} placeholder="2" min="0" max="15" style={{maxWidth:100}} />
              </div>
            )}
            <div className={styles.veld}><label>Adempatroon</label>
              <div className={styles.knoprij}>
                {['Normaal','Oppervlakkig','Onregelmatig'].map(o=>(
                  <button key={o} className={`${styles.keuzeKnopKlein} ${invoer.adempatroon===o?styles.geselecteerd:''} ${o!=='Normaal'?styles.waarschuwingKleur:''}`} onClick={()=>setVeld('adempatroon',o)}>{o}</button>
                ))}
              </div>
            </div>
            <div className={styles.tweeKolommen}>
              <div className={styles.veld}><label>Hulpademhalingsspieren?</label><JaNee veld="hulpademhalingsspieren" gevaar /></div>
              <div className={styles.veld}><label>Blauwverkleuring huid/lippen (cyanose)?</label><JaNee veld="cyanose" gevaar /></div>
            </div>
          </div>
        </div>
      )}

      {/* STAP 3: CIRCULATION */}
      {stap === 3 && (
        <div className={styles.stap}>
          <h2 className={styles.stapTitel}>C — Circulation</h2>
          <div className={styles.vitaalGrid}>
            <div className={`${styles.vitaalKaart} ${styles.vitaalBreed}`}>
              <label>Bloeddruk</label>
              <div className={styles.bdRij}>
                <input type="number" inputMode="numeric" value={invoer.bloeddruk_sys} onChange={e=>setVeld('bloeddruk_sys',e.target.value)} placeholder="120" />
                <span className={styles.bdSep}>/</span>
                <input type="number" inputMode="numeric" value={invoer.bloeddruk_dia} onChange={e=>setVeld('bloeddruk_dia',e.target.value)} placeholder="80" />
              </div>
              <span className={styles.eenheid}>mmHg — sys / dia</span><W veld="bloeddruk_sys" />
            </div>
            <div className={styles.vitaalKaart}>
              <label>Hartslag</label>
              <input type="number" inputMode="numeric" value={invoer.hartslag} onChange={e=>setVeld('hartslag',e.target.value)} placeholder="72" />
              <span className={styles.eenheid}>slagen/min</span><W veld="hartslag" />
            </div>
            <div className={styles.vitaalKaart}>
              <label>Temperatuur</label>
              <input type="number" inputMode="decimal" step="0.1" value={invoer.temperatuur} onChange={e=>setVeld('temperatuur',e.target.value)} placeholder="37.2" />
              <span className={styles.eenheid}>°C</span><W veld="temperatuur" />
            </div>
          </div>
          <div className={styles.kaart}>
            <div className={styles.veld}><label>Hartritme</label>
              <div className={styles.knoprij}>
                {['Regulair','Irregulair'].map(o=>(
                  <button key={o} className={`${styles.keuzeKnopKlein} ${invoer.hartritme===o?styles.geselecteerd:''} ${o==='Irregulair'?styles.waarschuwingKleur:''}`} onClick={()=>setVeld('hartritme',o)}>{o}</button>
                ))}
              </div>
            </div>
            <div className={styles.veld}><label>Huidkleur</label>
              <div className={styles.knoprij}>
                {['Normaal','Bleek','Rood','Grijs'].map(o=>(
                  <button key={o} className={`${styles.keuzeKnopKlein} ${invoer.huidkleur===o?styles.geselecteerd:''} ${o!=='Normaal'?styles.waarschuwingKleur:''}`} onClick={()=>setVeld('huidkleur',o)}>{o}</button>
                ))}
              </div>
            </div>
            <div className={styles.tweeKolommen}>
              <div className={styles.veld}><label>Capillaire refill</label>
                <div className={styles.knoprij}>
                  {['Normaal','Verlengd'].map(o=>(
                    <button key={o} className={`${styles.keuzeKnopKlein} ${invoer.capillaire_refill===o?styles.geselecteerd:''} ${o==='Verlengd'?styles.gevaar:''}`} onClick={()=>setVeld('capillaire_refill',o)}>{o}</button>
                  ))}
                </div>
              </div>
              <div className={styles.veld}><label>Zichtbare bloeding?</label><JaNee veld="bloeding" gevaar /></div>
            </div>
          </div>
        </div>
      )}

      {/* STAP 4: DISABILITY */}
      {stap === 4 && (
        <div className={styles.stap}>
          <h2 className={styles.stapTitel}>D — Disability</h2>
          <div className={styles.kaart}>
            <div className={styles.veld}><label>AVPU — Bewustzijn</label>
              <div className={styles.avpuRij}>
                {[{c:'A',l:'Alert',s:'Volledig bij bewustzijn'},{c:'V',l:'Verbaal',s:'Reageert op aanspreken'},{c:'P',l:'Pijn',s:'Reageert op pijnprikkel'},{c:'U',l:'Unresp.',s:'Niet reagerend'}].map(o=>(
                  <button key={o.c} className={`${styles.avpuKnop} ${invoer.avpu===o.c?styles.geselecteerd:''} ${o.c!=='A'?styles.gevaar:''}`} onClick={()=>setVeld('avpu',o.c)}>
                    <span className={styles.avpuLetter}>{o.c}</span>
                    <span className={styles.avpuLabel}>{o.l}</span>
                    <span className={styles.avpuSub}>{o.s}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
          <div className={styles.vitaalGrid}>
            <div className={styles.vitaalKaart}>
              <label>Bloedsuiker</label>
              <input type="number" inputMode="decimal" step="0.1" value={invoer.bloedsuiker} onChange={e=>setVeld('bloedsuiker',e.target.value)} placeholder="5.5" />
              <span className={styles.eenheid}>mmol/L</span><W veld="bloedsuiker" />
            </div>
            <div className={styles.vitaalKaart}>
              <label>Pijnscore NRS</label>
              <input type="number" inputMode="numeric" value={invoer.pijnscore} onChange={e=>setVeld('pijnscore',e.target.value)} placeholder="0" min="0" max="10" />
              <span className={styles.eenheid}>0 = geen · 10 = ergst</span><W veld="pijnscore" />
            </div>
          </div>
          <div className={styles.kaart}>
            <div className={styles.veld}><label>Pupilreactie</label>
              <div className={styles.knoprij}>
                {['Normaal','Afwijkend','Niet beoordeeld'].map(o=>(
                  <button key={o} className={`${styles.keuzeKnopKlein} ${invoer.pupilreactie===o?styles.geselecteerd:''} ${o==='Afwijkend'?styles.gevaar:''}`} onClick={()=>setVeld('pupilreactie',o)}>{o}</button>
                ))}
              </div>
            </div>
            <div className={styles.veld}><label>Nieuwe klachten zenuwstelsel? (bijv. verlamming, spraakproblemen)</label>
              <JaNee veld="neurologische_uitval" gevaar />
              {invoer.neurologische_uitval==='Ja' && (
                <textarea value={invoer.neurologische_uitval_omschrijving} onChange={e=>setVeld('neurologische_uitval_omschrijving',e.target.value)}
                  placeholder="Omschrijf de uitval..." className={styles.textarea} rows={3} />
              )}
            </div>
          </div>
        </div>
      )}

      {/* STAP 5: EXPOSURE */}
      {stap === 5 && (
        <div className={styles.stap}>
          <h2 className={styles.stapTitel}>E — Exposure</h2>
          <div className={styles.kaart}>
            <div className={styles.veld}><label>Huidafwijkingen of wonden?</label>
              <JaNee veld="huidafwijkingen" />
              {invoer.huidafwijkingen==='Ja' && (
                <textarea value={invoer.huidafwijkingen_omschrijving} onChange={e=>setVeld('huidafwijkingen_omschrijving',e.target.value)}
                  placeholder="Omschrijf locatie en aard..." className={styles.textarea} rows={2} />
              )}
            </div>
            <div className={styles.veld}><label>Zwelling of roodheid?</label>
              <JaNee veld="zwelling_roodheid" />
              {invoer.zwelling_roodheid==='Ja' && (
                <input type="text" value={invoer.zwelling_locatie} onChange={e=>setVeld('zwelling_locatie',e.target.value)}
                  placeholder="Locatie (bijv. linker enkel)" className={styles.inputMarginTop} />
              )}
            </div>
          </div>
          <div className={styles.kaart}>
            <div className={styles.veld}><label>Reden van bellen / recente gebeurtenissen <span className={styles.optioneel}>(bijv. val, ingreep, koorts, plotselinge verslechtering)</span></label>
              <textarea value={invoer.recente_gebeurtenissen} onChange={e=>setVeld('recente_gebeurtenissen',e.target.value)}
                placeholder="bijv. gevallen vanmorgen..." className={styles.textarea} rows={2} />
            </div>
            <div className={styles.veld}><label>Bekende diagnoses / voorgeschiedenis</label>
              <textarea value={invoer.voorgeschiedenis} onChange={e=>setVeld('voorgeschiedenis',e.target.value)}
                placeholder="bijv. CVA 2021, DM type 2..." className={styles.textarea} rows={3} />
            </div>
            <div className={styles.veld}><label>Actuele relevante medicatie <span className={styles.optioneel}>(optioneel)</span></label>
              <textarea value={invoer.medicatie} onChange={e=>setVeld('medicatie',e.target.value)}
                placeholder="bijv. metformine, furosemide..." className={styles.textarea} rows={2} />
            </div>
          </div>
        </div>
      )}

      {/* STAP 6: AFSLUITING */}
      {stap === 6 && (
        <div className={styles.stap}>
          <h2 className={styles.stapTitel}>Afsluiting</h2>
          {vervolgvragen.length > 0 && (
            <div className={styles.kaart}>
              <p className={styles.vervolgTitel}>📋 Aanvullende vragen op basis van uw invoer:</p>
              {vervolgvragen.map(v => (
                <div key={v.veld} className={styles.veld}>
                  <label>{v.vraag}</label>
                  {v.type==='ja_nee' && <JaNee veld={v.veld} />}
                  {v.type==='keuze' && (
                    <div className={styles.knoprij}>
                      {v.opties.map(o=>(
                        <button key={o} className={`${styles.keuzeKnopKlein} ${invoer[v.veld]===o?styles.geselecteerd:''}`} onClick={()=>setVeld(v.veld,o)}>{o}</button>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
          <div className={styles.kaart}>
            <label className={styles.groepLabel}>Urgentie</label>
            <div className={styles.urgentieRij}>
              {[{c:'acuut',l:'🔴 Acuut',s:'Direct handelen vereist'},{c:'spoed',l:'🟡 Spoed',s:'Snel contact gewenst'},{c:'niet_spoedeisend',l:'🟢 Niet spoedeisend',s:'Kan wachten'}].map(o=>(
                <button key={o.c} className={`${styles.urgentieKnop} ${urgentie===o.c?styles.geselecteerd:''}`} onClick={()=>valideerUrgentie(o.c)}>
                  <span>{o.l}</span><span className={styles.urgentieSub}>{o.s}</span>
                </button>
              ))}
            </div>
            {urgentieWaarschuwing && <div className={`${styles.waarschuwing} ${styles.afwijkend}`}>🤖 {urgentieWaarschuwing}</div>}
          </div>
          <div className={styles.kaart}>
            <label className={styles.groepLabel}>Outputtype</label>
            <div className={styles.outputGrid}>
              {[{c:'sbar',l:'SBAR'},{c:'artsconsult',l:'Artsconsult'},{c:'rapportage',l:'Verpleegkundige rapportage'},{c:'dienst_overdracht',l:'Dienst-overdracht'},{c:'familie',l:'Familiecommunicatie'},{c:'overplaatsing',l:'Overplaatsingsoverdracht'}].filter(o=>heeftModule(`outputs.${o.c}`)).map(o=>(
                <button key={o.c} className={`${styles.outputKnop} ${outputType===o.c?styles.geselecteerd:''}`} onClick={()=>setOutputType(o.c)}>{o.l}</button>
              ))}
            </div>
          </div>
          <div className={styles.kaart}>
            <label className={styles.groepLabel}>Verzoek</label>
            <div className={styles.knoprij}>
              {['Telefonisch advies','Visite','Anders'].map(o=>(
                <button key={o} className={`${styles.keuzeKnopKlein} ${verzoek===o?styles.geselecteerd:''}`} onClick={()=>setVerzoek(o)}>{o}</button>
              ))}
            </div>
          </div>
          {/* Korte omschrijving voor opslaan */}
          <div className={styles.kaart}>
            <label className={styles.groepLabel}>
              Korte omschrijving voor opslaan <span style={{color:'#CC0000'}}>*</span>
              <span style={{fontWeight:400,color:'#999',fontSize:12,display:'block',marginTop:2}}>
                bijv. benauwdheid, vallen, pijn op de borst
              </span>
            </label>
            <input
              type="text"
              className={styles.omschrijvingVeld}
              value={omschrijving}
              onChange={e => setOmschrijving(e.target.value.slice(0, 50))}
              placeholder="Max. 50 tekens"
              maxLength={50}
            />
            <div style={{fontSize:11,color:'#999',textAlign:'right',marginTop:4}}>{omschrijving.length}/50</div>
          </div>

          {fout && <div className={styles.foutMelding}>{fout}</div>}
          <button className={styles.genereerKnop} onClick={handleGenereer} disabled={laden}>
            {laden ? 'Bezig met genereren...' : `${outputType.toUpperCase()} genereren →`}
          </button>
        </div>
      )}

      {/* Navigatie */}
      <div className={styles.navigatie}>
        {stap > 0 && <button className={styles.terugKnop} onClick={terug}>← Terug</button>}
        {stap < 6 && <button className={styles.volgendeKnop} onClick={volgende}>Volgende: {STAPPEN[stap+1]?.label} →</button>}
      </div>
    </div>
  );
}
